/**
 * Ensures that the given path string is a valid internal route with a leading slash.
 * Rejects external URLs, protocol handlers, and path traversal attempts.
 *
 * **Security**: This function prevents open redirect vulnerabilities by validating
 * that the path is an internal relative route only. Used in authentication flows
 * where user-controlled paths could be exploited.
 *
 * @param path - The input path string from user input (e.g., query parameters).
 * @returns The validated path string guaranteed to start with a leading slash.
 * @throws Error if the path contains security risks (external URLs, protocols, path traversal).
 *
 * @example
 * sanitizePath("/app/home") // => "/app/home"
 * sanitizePath("app/home")  // => "/app/home"
 * sanitizePath("")          // => "/"
 * sanitizePath("//evil.com") // throws Error
 * sanitizePath("http://evil.com") // throws Error
 * sanitizePath("../../../etc/passwd") // throws Error
 */
export default function sanitizePath(path: string): string {
  // Trim whitespace from the path
  const trimmedPath = path.trim();

  // If the path is empty after trimming, return the root path
  if (trimmedPath === "") {
    return "/";
  }

  // Security: Reject backslashes FIRST (before protocol check)
  // Some browsers interpret backslashes as forward slashes
  // Must check before protocol regex to catch Windows paths like "C:\..."
  if (trimmedPath.includes("\\")) {
    throw new Error("Invalid path: backslashes are not allowed");
  }

  // Security: Reject path traversal attempts (..)
  // Prevents directory traversal attacks
  if (trimmedPath.includes("..")) {
    throw new Error("Invalid path: path traversal is not allowed");
  }

  // Security: Reject absolute URLs with protocols (http:, https:, javascript:, data:, etc.)
  // Pattern matches: protocol-name followed by colon
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmedPath)) {
    throw new Error(
      "Invalid path: absolute URLs with protocols are not allowed"
    );
  }

  // Security: Reject protocol-relative URLs (//example.com), but allow paths with only slashes
  // Protocol-relative URLs bypass same-origin policy and enable open redirects
  // Exception: paths like "///" are just multiple slashes and should normalize to "/"
  if (trimmedPath.startsWith("//") && !/^\/+$/.test(trimmedPath)) {
    throw new Error("Invalid path: protocol-relative URLs are not allowed");
  }

  // Normalize: Collapse multiple slashes and ensure leading slash
  // "/app//home///page" => "/app/home/page"
  // "///" => "/"
  const segments = trimmedPath.split("/").filter((segment) => segment !== "");
  const normalizedPath = segments.length > 0 ? `/${segments.join("/")}` : "/";

  return normalizedPath;
}
